import type { Currency } from "@/shared/value-objects/currency.vo";
import {
  type ControlResultColumnResponseDto,
  ControlResultColumnResponseDtoType,
  type ControlResultListItemResponseDto,
} from "@/spz-api/model";
import type {
  Column,
  PaginationState,
  RowSelectionState,
} from "@tanstack/react-table";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { ControlResultDynamicFilterPopover } from "../components/control-result-dynamic-filter-popover";
import { useBulkSelectionStore } from "../store/bulk-selection.store";
import { useControlResultFiltersSyncWithUrl } from "./use-control-result-filters-sync-with-url";

interface UseControlResultsTableProps {
  data: ControlResultListItemResponseDto[];
  columns: Column<ControlResultListItemResponseDto, unknown>[];
  rowSelection: RowSelectionState;
  setRowSelection: (
    updater:
      | RowSelectionState
      | ((old: RowSelectionState) => RowSelectionState),
  ) => void;
  pagination: PaginationState;
  setPagination: (
    updater: PaginationState | ((old: PaginationState) => PaginationState),
  ) => void;
  totalCount: number;
  controlResultColumns: ControlResultColumnResponseDto[];
  t: TFunction;
  currency: Currency | undefined;
}

export const useControlResultsTable = ({
  data,
  columns,
  rowSelection,
  setRowSelection,
  pagination,
  setPagination,
  totalCount,
  controlResultColumns,
  t,
  currency,
}: UseControlResultsTableProps) => {
  const [filtersResetVersion, setFiltersResetVersion] = useState(0);
  const [columnSizing, setColumnSizing] = useState({});
  const prevHadFiltersRef = useRef(false);
  const { selectionEnabled } = useBulkSelectionStore();
  // Filterable columns
  const numericAndDateColumns = useMemo(
    () =>
      controlResultColumns.filter((col) => {
        const type = col.type.toLowerCase();
        return [
          ControlResultColumnResponseDtoType.INTEGER,
          ControlResultColumnResponseDtoType.SHORT_DATE,
          ControlResultColumnResponseDtoType.MONETARY,
          ControlResultColumnResponseDtoType.TEXT,
        ].some((t) => t.toLowerCase() === type);
      }),
    [controlResultColumns],
  );

  const rangeColumns = useMemo(
    () =>
      controlResultColumns.filter((col) => {
        const type = col.type.toLowerCase();
        return [
          ControlResultColumnResponseDtoType.INTEGER,
          ControlResultColumnResponseDtoType.SHORT_DATE,
          ControlResultColumnResponseDtoType.MONETARY,
        ].some((t) => t.toLowerCase() === type);
      }),
    [controlResultColumns],
  );

  const numericColumns = useMemo(
    () =>
      numericAndDateColumns
        .filter((col) => {
          const type = col.type.toLowerCase();
          return [
            ControlResultColumnResponseDtoType.INTEGER,
            ControlResultColumnResponseDtoType.MONETARY,
          ].some((t) => t.toLowerCase() === type);
        })
        .map((col) => col.columnKey),
    [numericAndDateColumns],
  );

  // Filters sync with URL
  const urlKeys = useMemo(() => {
    const columnKeys = [
      ...numericAndDateColumns.map((c) => c.columnKey),
      "workflow_step_uuid",
      "conclusion_uuid",
      "conclusion_category_uuid",
      "owner_uuid",
      "priority_uuid",
    ];
    return Object.fromEntries(columnKeys.map((k) => [k, k]));
  }, [numericAndDateColumns]);

  const { columnFilters, globalFilter, setColumnFilters, sorting, setSorting } =
    useControlResultFiltersSyncWithUrl({
      urlKeys,
      rangeColumns: rangeColumns.map((c) => c.columnKey),
      numericColumns,
    });

  // Track filter resets
  useEffect(() => {
    const hasFilters = columnFilters.length > 0;
    if (prevHadFiltersRef.current && !hasFilters) {
      setFiltersResetVersion((v) => v + 1);
    }
    prevHadFiltersRef.current = hasFilters;
  }, [columnFilters]);

  // Table instance
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    columnResizeMode: "onChange",
    enableColumnResizing: true,
    initialState: {
      columnPinning: {
        right: ["actions"],
        left: selectionEnabled ? ["selection", "id"] : ["id"],
      },
    },
    state: {
      globalFilter,
      rowSelection,
      pagination,
      columnFilters,
      columnPinning: {
        left: ["selection", "resultGroupId"],
      },
      columnSizing,
      sorting,
    },
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: (updater) => {
      setColumnFilters(updater);
      table.resetPageIndex();
    },
    onColumnSizingChange: setColumnSizing,
    onSortingChange: (updater) => {
      setSorting(updater);
      table.resetPageIndex();
    },
    getRowId: (row) => row.id,
    enableMultiRowSelection: true,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    rowCount: totalCount,
  });

  // Dynamic filters
  const dynamicFilters = useMemo(() => {
    return numericAndDateColumns.map((col) => {
      const column = table.getColumn(col.columnKey);
      if (!column) return null;
      return (
        <ControlResultDynamicFilterPopover
          key={`${col.columnKey}-${filtersResetVersion}`}
          column={column}
          label={t(col.title)}
          type={col.type}
          currency={currency}
        />
      );
    });
  }, [numericAndDateColumns, table, filtersResetVersion, t, currency]);

  return {
    table,
    dynamicFilters,
  };
};
